<template>
    <b-row class="mb-3">
        <b-card no-body bg-variant="white" class="w-100">
            <template #header>
                <div class="d-flex align-items-center justify-content-between">
                    <h5 class="mb-0">조건 검색하기</h5>

                    <div v-if="listLoading">
                        <div class="d-flex align-items-center" style="height: 36px">
                            <b-spinner class="mr-2" variant="primary" style="height: 1rem; width: 1rem;"/>
                            <p class="d-inline-block mb-0">조회중...</p>
                        </div>
                    </div>
                    <div v-else class="d-flex align-items-center">
                        <b-button form="condition-search-form"
                                  type="reset"
                                  class="mr-2"
                                  :disabled="listLoading"
                        >
                            {{ $t('초기화') }}
                        </b-button>
                        <b-button
                            variant="primary"
                            :disabled="listLoading"
                            form="condition-search-form"
                            type="submit"
                        >
                            {{ $t('검색하기') }}
                        </b-button>
                    </div>
                </div>
            </template>

            <div class="p-3">
                <b-form id="condition-search-form"
                        autocomplete="new-password"
                        @submit.stop.prevent="searchData"
                        @reset.prevent="resetSearch"
                >
                    <b-row>
                        <b-col cols="12" class="mb-3 mb-xl-0">
                            <b-form-group
                                id="fieldset-input-name"
                                label="검색어"
                                label-for="input-name"
                                class="mb-0"
                            >
                                <b-form-input id="input-name"
                                              v-model="searchFormValue.search"
                                              :disabled="listLoading"
                                />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-form>

            </div>
        </b-card>


        <b-row class="mt-3">
            <b-col cols="12">
                <b-card
                    :class="[{ 'shadow-sm': searchResults || (!listLoading && searchResults) }, 'list-container']"
                    :bg-variant="!searchResults ? 'light' : 'white'"
                    no-body
                >
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between">
                            <div>
                                <h6 class="mb-0">조건 목록</h6>
                            </div>
                            <div class="d-flex align-items-center">
                                <b-button variant="outline-primary" size="md"
                                          @click="openConditionModal('add')">
                                    조건 추가
                                </b-button>
                            </div>
                        </div>
                    </template>
                    <div class="table-container">
                        <!-- DS 표 규칙: 행 44 고정 — 칸마다 한 줄, 넘치면 줄이고 전체는 title. 폭은 머리가 정한다 -->
                        <b-table
                            responsive
                            table-class="ds-table ds-table-fixed condition-list-table"
                            :items="searchResults"
                            :fields="tableColumns"
                            class="mb-0"
                            show-empty
                            :busy="listLoading"
                        >
                            <template #empty>
                                <PageEmptyComponent
                                    :text="$t('조회된 조건이 없습니다.')"
                                    :loading="listLoading"
                                    :loadingText="$t('조건 목록을 조회 중입니다.')"
                                    :error="listError"
                                    @retry="fetchData"
                                />
                            </template>
                            <!-- 메인 칸 = 상세 창 -->
                            <template #cell(name)="row">
                                <button type="button" class="ds-detail-link ds-ellipsis" :title="row.value"
                                        @click="openConditionModal('detail', row.item)">{{ row.value }}</button>
                            </template>

                            <template #cell(key)="row">
                                <code class="ds-ellipsis" :title="row.value">{{ row.value }}</code>
                            </template>

                            <template #cell(operator)="row">
                                <span v-if="row.value" class="ds-tag">{{ row.value }}</span>
                            </template>

                            <template #cell(updated_at)="row">
                                {{ get_date(row.value) }}
                            </template>

                            <template #head(detail)="data">
                                <span class="sr-only">{{ data.label }}</span>
                            </template>
                            <template #cell(detail)="row">
                                <button type="button" class="ds-row-icon-btn"
                                        :aria-label="`${row.item.name} 조건 상세`" title="조건 상세"
                                        @click="openConditionModal('detail', row.item)">
                                    <AppIcon name="chevron-right"/>
                                </button>
                            </template>
                        </b-table>

                        <div class="pagination-container">
                            <b-pagination
                                ref="pagination"
                                v-model="pagination.currentPage"
                                :total-rows="pagination.totalRows"
                                :per-page="pagination.perPage"
                                first-number
                                last-number
                                :disabled="listLoading"
                                align="fill"
                                size="sm"
                                @change="(page)=>changeQuery({page})"
                            ></b-pagination>
                            <b-form-select
                                v-model="pagination.perPage"
                                class="ml-3 mb-3"
                                style="width: 100px"
                                size="sm"
                                :disabled="listLoading"
                                @change="(pageSize)=>changeQuery({pageSize})"
                            >
                                <b-form-select-option v-for="data in perPageOptions"
                                                      :key="`${data.text}-${data.value}`"
                                                      :value="data.value">{{ data.text }}
                                </b-form-select-option>
                            </b-form-select>

                        </div>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <ConditionModal ref="conditionModal"
                        :detail-id="detailId"
                        @updateDetailData="updateDetailData"
                        @addData="initPage"
                        @deleteData="initPage"
        />

    </b-row>
</template>

<script>
import {listConditions} from '@shared_utils/api/ruleEngine';
import {getErrorMessage} from '@core/utils/api/errorMessage';
import {
    getDefaultPagination,
    onTooltipShow
} from '@shared/components/admin/pages/Management/common-data';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import PaginationPageMixin from '@shared/mixin/pagination-page-mixins';
import {listColumns, searchFormValue} from '@shared/components/admin/pages/Management/CampaignConnectRule/Condition/data';
import ConditionModal from '@shared/components/admin/pages/Management/CampaignConnectRule/Condition/ConditionModal.vue';

export default {
    name: 'ManageCondition',
    components: {
        ConditionModal,
        PageEmptyComponent,
    },
    mixins: [PaginationPageMixin({searchForm: searchFormValue, pagination: getDefaultPagination(1, 20)})],
    props: {
        currentTab: {
            type: Number,
            default: 0
        }
    },

    data() {
        return {
            tableColumns: listColumns,
            detailId: null,
            listError: '',
        };
    },
    watch: {
        '$route': {
            handler() {
                // 첫 탭이라 URL 에 tab 이 없으면 이 탭이다
                if ((this.$route.query.tab || 'condition') === 'condition') {
                    this.setSearchDataFormQuery();
                    this.fetchData();
                }
            }
        }
    },
    async mounted() {
        if ((this.$route.query.tab || 'condition') !== 'condition') return;

        this.setSearchDataFormQuery();
        await this.fetchData();
    },
    methods: {
        onTooltipShow,
        async searchData() {
            this.pagination.currentPage = 1;
            const checkSameQuery = await this.changeQuery({});
            if (checkSameQuery) {
                await this.fetchData();
            }
        },
        initPage() {
            this.resetForm();
            this.setSearchDataFormQuery();
            this.fetchData();
        },
        async fetchData() {
            try {
                this.listLoading = true;
                this.listError = '';

                const res = await listConditions({
                    params: {
                        ...this.searchFormValue,
                        page: this.pagination.currentPage,
                        per_page: this.pagination.perPage,
                    },
                });

                if (res.data == null) {
                    this.searchResults = [];
                    this.pagination.totalRows = 0;
                    return;
                }
                const {conditions, total, current_page} = res.data;

                this.searchResults = conditions.map((item, index) => {
                    const data = {
                        ...item,
                        value: JSON.stringify(item.value),
                        // 번호는 응답 페이지 기준 — 새로고침 직후엔 페이지 버튼이 응답 전에 잠시 1 로 깎는다
                        index: this.pagination.perPage * ((Number(current_page) || 1) - 1) + index + 1,
                    };
                    return data;
                });

                this.pagination.totalRows = total;
                this.pagination.currentPage = null;
                setTimeout(() => {
                    this.pagination.currentPage = Number(current_page);
                });
            } catch (error) {
                // 실패를 '조회된 조건이 없습니다'로 보이지 않게 이전 결과를 비우고 표 안에 오류를 띄운다
                this.searchResults = [];
                this.pagination.totalRows = 0;
                if (error?.response?.status === 401) return;
                this.listError = `조건 목록을 불러오지 못했습니다. ${getErrorMessage(error, '')}`.trim();
                this.$EventBus.$emit('toast-failure', this.$t('검색 오류'), '조건 검색 도중 오류가 발생했습니다. 잠시후 재시도 해주세요.');
            } finally {
                this.listLoading = false;
            }
        },
        async openConditionModal(mode, item) {
            if (mode === 'add') {
                this.detailId = null;
                this.$refs.conditionModal.changeMode(mode);
                this.$bvModal.show('condition-detail-modal');
                return;
            }


            this.detailId = item.id;
            this.$refs.conditionModal.changeMode(mode);
            this.$bvModal.show('condition-detail-modal');

        },
        updateDetailData(updatedItem) {
            const idx = this.searchResults.findIndex((item) => {
                return item.id === updatedItem.id;
            });
            // 번호는 목록에서 매긴 값을 그대로(다시 매기면 여러 페이지일 때 그 행만 페이지 안 번호로 바뀌었다)
            this.$set(this.searchResults, idx, {...updatedItem, index: this.searchResults[idx]?.index});
        }
    }
};
</script>

<style lang="scss">
/* 고정 열 합 372(No. 64 · 연산자 88 · 수정일시 160 · 상세 60) + 조건명 · 조건 키 ~194씩.
   1366(메뉴 펼침) 본문 1074 - 탭 여백 40 + 행 · 칸 여백 상쇄 30 - 카드 테두리 2 = 표 폭 약 1062 안 — 그보다 좁으면 가로 스크롤 */
.condition-list-table {
    min-width: 760px;
}
</style>
